import {
  TLoginApiPayload,
  TManageCartApiPayload,
  TManageCartApiResponse,
  TRegisterApiPayload,
  TSavedCardPayload,
  TUpdateSubscriptionDetailsPayload,
  TUpdateProfile,
  TEkataScoreRequest,
  TEkataScoreResponse,
  TValidateGroupRestrictionsResponse,
  TUpdateAttendancePayload,
  TUpdateAttendanceResponse,
  TCheckoutApiResponse,
  TExchangeReservationPayload,
} from "src/shared/types/api.types";
import { useTeamConfig } from "../components/TeamConfigProvider";
import { useZynk } from "./useZynk";
import {
  TCancellationDetails,
  TGetUpcomingEventsResponse,
  TGroupDetails,
  TSubscriptionDetails,
  TAdditionalDetails,
  TSwafConfig,
  TCreateListingPayload,
  TCreateListingResponse,
  TGetPotentialListingsResponse,
  TTeamInfoResponse,
  TDeleteListingsPayload,
  TDeleteListingsResponse,
  TEditListingPayload,
  TEditListingResponse,
  TBundle,
  TPassOnAccount,
  TEventActivityHistory,
  TPass,
} from "src/shared/types/globalStotre.types";
import { global_config } from "src/shared/global_config";
import type {
  TListingEvent,
  TMarketplacePurchaseHistory,
} from "src/shared/types/marketplace.types";
import { TGiftCodeDetails } from "src/components/feature/giftFlow/types";

export const api_base = global_config.BACKEND_URL;

const createEndpoints = (teamSchema: string) => {
  return {
    ping: `${api_base}/${teamSchema}/auth/ping`,
    getPasses: `${api_base}/${teamSchema}/pass/getPasses`,
    getPassById: `${api_base}/${teamSchema}/pass/getPassById`,
    getPassesOnAccount: `${api_base}/${teamSchema}/pass/getPassesOnAccount`,
    tmAuthUrl: `${api_base}/${teamSchema}/auth/tmAuthURL`,
    login: `${api_base}/${teamSchema}/auth/login`,
    register: `${api_base}/${teamSchema}/auth/register`,
    refreshSession: `${api_base}/${teamSchema}/auth/refreshSession`,
    logout: `${api_base}/${teamSchema}/auth/logout`,
    updateProfile: `${api_base}/${teamSchema}/auth/updateProfile`,
    seatGeekAuthURL: `${api_base}/${teamSchema}/auth/seatGeekAuthURL`,
    manageCart: `${api_base}/${teamSchema}/cart/manageCart`,
    cancelCart: `${api_base}/${teamSchema}/cart/cancelCart`,
    checkout: `${api_base}/${teamSchema}/cart/checkout`,
    getUserGroup: `${api_base}/${teamSchema}/swaf/getUserGroup`,
    createGroup: `${api_base}/${teamSchema}/swaf/createGroup`,
    removeGroupMember: `${api_base}/${teamSchema}/swaf/removeGroupMember`,
    leaveGroup: `${api_base}/${teamSchema}/swaf/leaveGroup`,
    joinGroup: `${api_base}/${teamSchema}/swaf/joinGroup`,
    getGroupDetails: `${api_base}/${teamSchema}/swaf/getGroupDetails`,
    getCard: `${api_base}/${teamSchema}/card/getCard`,
    saveCard: `${api_base}/${teamSchema}/card/saveCard`,
    getSubscriptionDetails: `${api_base}/${teamSchema}/subscription/getSubscriptionDetails`,
    addCancellationDetails: `${api_base}/${teamSchema}/subscription/addCancellationDetails`,
    updateSubscriptionDetails: `${api_base}/${teamSchema}/subscription/updateSubscriptionDetails`,
    updateAttendance: `${api_base}/${teamSchema}/event/updateAttendance`,
    updateAttendanceWithAssignment: `${api_base}/${teamSchema}/event/updateAttendanceWithAssignment`,
    getUpcomingEvents: `${api_base}/${teamSchema}/event/getUpcomingEvents`,
    getPurchaseHistory: `${api_base}/${teamSchema}/transaction/getTransactionHistory`,
    getEventActivityHistory: `${api_base}/${teamSchema}/transaction/getEventActivityHistory`,
    getUserRewardInformation: `${api_base}/${teamSchema}/reward/getUserRewardInformation`,
    findAndRetrieveEkataScore: `${api_base}/${teamSchema}/cart/findAndRetrieveEkataScore`,
    getAdditionalDetails: `${api_base}/${teamSchema}/additional-details/getAdditionalDetails`,
    insertAdditionalDetailsAnswers: `${api_base}/${teamSchema}/additional-details/insertAdditionalDetailsAnswers`,
    getSwafStatus: `${api_base}/${teamSchema}/swaf/getSwafStatus`,
    validateGroupRestrictions: `${api_base}/${teamSchema}/swaf/validateGroupRestrictions`,
    getMerchantSession: `${api_base}/${teamSchema}/marketplace/getMerchantSession`,
    updateMerchantKycStatus: `${api_base}/${teamSchema}/marketplace/updateMerchantKycStatus`,
    getMerchantAccountDetails: `${api_base}/${teamSchema}/marketplace/getMerchantAccountDetails`,
    getCustomerSession: `${api_base}/${teamSchema}/marketplace/getCustomerSession`,
    getMarketplacePurchaseHistory: `${api_base}/${teamSchema}/marketplace/getMarketplaceTransactionHistory`,
    createPaymentIntent: `${api_base}/${teamSchema}/marketplace/createPaymentIntent`,
    distributeMerchantPayment: `${api_base}/${teamSchema}/marketplace/distributeMerchantPayment`,
    getPotentialListings: `${api_base}/${teamSchema}/marketplace/getPotentialListings`,
    submitPotentialListing: `${api_base}/${teamSchema}/marketplace/createListings`,
    getListingsForSale: `${api_base}/${teamSchema}/marketplace/getListingsForSale`,
    getListingsByIds: `${api_base}/${teamSchema}/marketplace/getListingsByIds`,
    deleteListings: `${api_base}/${teamSchema}/marketplace/deleteListings`,
    editListing: `${api_base}/${teamSchema}/marketplace/editListing`,
    getTeamInfo: `${api_base}/${teamSchema}/team/getTeamInfo`,
    marketplaceManageCart: `${api_base}/${teamSchema}/marketplace/manageCart`,
    marketplaceCancelCart: `${api_base}/${teamSchema}/marketplace/cancelCart`,
    marketplaceCheckout: `${api_base}/${teamSchema}/marketplace/checkout`,
    getBundles: `${api_base}/${teamSchema}/bundles/getPassBundles`,
    // exchange reservation
    exchangeReservation: `${api_base}/${teamSchema}/event/exchangeReservation`,
    // gift flow
    validateRedemptionCode: `${api_base}/${teamSchema}/gift/validateRedemptionCode`,
    redeemPassGift: `${api_base}/${teamSchema}/gift/redeemPassGift`,
  };
};

// prettier-ignore
export function useApiRoutes() {
  const teamConfig = useTeamConfig();
  const apiRoutes = createEndpoints(teamConfig.TEAM_SCHEMA);

  const pingApi = useZynk(apiRoutes.ping);
  const getPassesApi = useZynk<any, TPass[]>(apiRoutes.getPasses);
  const getPassByIdApi = useZynk<any, TPass>(apiRoutes.getPassById);
  const manageCartApi = useZynk<TManageCartApiPayload, TManageCartApiResponse>(apiRoutes.manageCart);
  const cancelCartApi = useZynk(apiRoutes.cancelCart);
  const checkoutApi = useZynk<any, TCheckoutApiResponse>(apiRoutes.checkout);
  const getTmAuthUrlApi = useZynk(apiRoutes.tmAuthUrl);
  const loginApi = useZynk<TLoginApiPayload>(apiRoutes.login);
  const registerApi = useZynk<TRegisterApiPayload>(apiRoutes.register);
  const updateProfileApi = useZynk<TUpdateProfile>(apiRoutes.updateProfile);
  const seatGeekAuthURLApi = useZynk(apiRoutes.seatGeekAuthURL);
  const refreshSessionApi = useZynk(apiRoutes.refreshSession);
  const logoutApi = useZynk(apiRoutes.logout);
  const getUserGroupApi = useZynk<any, TGroupDetails>(apiRoutes.getUserGroup);
  const getGroupDetailsApi = useZynk(apiRoutes.getGroupDetails);
  const getCardApi = useZynk(apiRoutes.getCard);
  const saveCardApi = useZynk<TSavedCardPayload>(apiRoutes.saveCard);
  const getSubscriptionDetailsApi = useZynk<TSubscriptionDetails>(apiRoutes.getSubscriptionDetails);
  const addCancellationDetailsApi = useZynk<TCancellationDetails>(apiRoutes.addCancellationDetails);
  const updateSubscriptionDetailsApi =useZynk<TUpdateSubscriptionDetailsPayload>(apiRoutes.updateSubscriptionDetails);
  const updateAttendanceApi = useZynk<TUpdateAttendancePayload, TUpdateAttendanceResponse>(apiRoutes.updateAttendance);
  const updateAttendanceWithAssignmentApi = useZynk<TUpdateAttendancePayload, TUpdateAttendanceResponse>(apiRoutes.updateAttendanceWithAssignment);
  const createGroupApi = useZynk(apiRoutes.createGroup);
  const removeGroupMemberApi = useZynk(apiRoutes.removeGroupMember);
  const leaveGroupApi = useZynk(apiRoutes.leaveGroup);
  const joinGroupApi = useZynk(apiRoutes.joinGroup);
  const getPassesOnAccountApi = useZynk<any, TPassOnAccount[]>(apiRoutes.getPassesOnAccount);
  const getUpcomingEventsApi = useZynk<any, TGetUpcomingEventsResponse>(apiRoutes.getUpcomingEvents);
  const getPurchaseHistoryApi = useZynk(apiRoutes.getPurchaseHistory);
  const getEventActivityHistoryApi = useZynk<any, TEventActivityHistory[]>(apiRoutes.getEventActivityHistory);
  const getUserRewardInformationApi = useZynk(apiRoutes.getUserRewardInformation);
  const findAndRetrieveEkataScoreApi = useZynk<TEkataScoreRequest,TEkataScoreResponse>(apiRoutes.findAndRetrieveEkataScore);
  const getAdditionalDetailsApi = useZynk<TAdditionalDetails>(apiRoutes.getAdditionalDetails);
  const insertAdditionalDetailsAnswersApi = useZynk(apiRoutes.insertAdditionalDetailsAnswers);
  const getSwafStatusApi = useZynk<any, TSwafConfig>(apiRoutes.getSwafStatus);
  const validateGroupRestrictionsApi = useZynk<any,TValidateGroupRestrictionsResponse>(apiRoutes.validateGroupRestrictions);
  const getMerchantSessionApi = useZynk(apiRoutes.getMerchantSession);
  const updateMerchantKycStatusApi = useZynk(apiRoutes.updateMerchantKycStatus);
  const getMerchantAccountDetailsApi = useZynk(apiRoutes.getMerchantAccountDetails);
  const getCustomerSessionApi = useZynk(apiRoutes.getCustomerSession);
  const getMarketplacePurchaseHistoryApi = useZynk<any, TMarketplacePurchaseHistory>(
    apiRoutes.getMarketplacePurchaseHistory,
  );
  const createPaymentIntentApi = useZynk(apiRoutes.createPaymentIntent);
  const distributeMerchantPaymentApi = useZynk(apiRoutes.distributeMerchantPayment);
  const getPotentialListingsApi = useZynk<any, TGetPotentialListingsResponse>(apiRoutes.getPotentialListings);
  const submitPotentialListingApi = useZynk<TCreateListingPayload, TCreateListingResponse>(apiRoutes.submitPotentialListing);
  const getListingsForSaleApi = useZynk<any, TListingEvent[]>(apiRoutes.getListingsForSale);
  const deleteListingsApi = useZynk<TDeleteListingsPayload, TDeleteListingsResponse>(apiRoutes.deleteListings);
  const editListingApi = useZynk<TEditListingPayload, TEditListingResponse>(apiRoutes.editListing);
  const getTeamInfoApi = useZynk<any, TTeamInfoResponse>(apiRoutes.getTeamInfo);
  const marketplaceManageCartApi = useZynk(apiRoutes.marketplaceManageCart);
  const marketplaceCancelCartApi = useZynk(apiRoutes.marketplaceCancelCart);
  const marketplaceCheckoutApi = useZynk(apiRoutes.marketplaceCheckout);
  const getBundlesApi = useZynk<any, TBundle[]>(apiRoutes.getBundles);
  const getListingsByIdsApi = useZynk<any, TListingEvent[]>(apiRoutes.getListingsByIds);
  const validateRedemptionCodeApi = useZynk<any, TGiftCodeDetails>(apiRoutes.validateRedemptionCode);
  const redeemPassGiftApi = useZynk<any, any>(apiRoutes.redeemPassGift);
  const exchangeReservationApi = useZynk<TExchangeReservationPayload, { message: string; wasNonContiguous: boolean }>(apiRoutes.exchangeReservation);


  return {
    apiRoutes,
    pingApi,
    getPassesApi,
    getPassByIdApi,
    manageCartApi,
    cancelCartApi,
    checkoutApi,
    getTmAuthUrlApi,
    loginApi,
    registerApi,
    refreshSessionApi,
    logoutApi,
    updateProfileApi,
    seatGeekAuthURLApi,
    getUserGroupApi,
    getCardApi,
    saveCardApi,
    createGroupApi,
    removeGroupMemberApi,
    leaveGroupApi,
    joinGroupApi,
    updateAttendanceApi,
    updateAttendanceWithAssignmentApi,
    getGroupDetailsApi,
    getSubscriptionDetailsApi,
    addCancellationDetailsApi,
    updateSubscriptionDetailsApi,
    getPassesOnAccountApi,
    getUpcomingEventsApi,
    getPurchaseHistoryApi,
    getEventActivityHistoryApi,
    getUserRewardInformationApi,
    findAndRetrieveEkataScoreApi,
    getAdditionalDetailsApi,
    insertAdditionalDetailsAnswersApi,
    getSwafStatusApi,
    validateGroupRestrictionsApi,
    getMerchantSessionApi,
    updateMerchantKycStatusApi,
    getMerchantAccountDetailsApi,
    getCustomerSessionApi,
    getMarketplacePurchaseHistoryApi,
    createPaymentIntentApi,
    distributeMerchantPaymentApi,
    getPotentialListingsApi,
    submitPotentialListingApi,
    getListingsForSaleApi,
    deleteListingsApi,
    editListingApi,
    getTeamInfoApi,
    marketplaceManageCartApi,
    marketplaceCancelCartApi,
    marketplaceCheckoutApi,
    getBundlesApi,
    getListingsByIdsApi,
    validateRedemptionCodeApi,
    redeemPassGiftApi,
    exchangeReservationApi,
  };
}
